import { zodResolver } from "@hookform/resolvers/zod"
import { Box, Dialog, DialogActions, DialogContent } from "@mui/material"
import { AddOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import React, { useState } from "react"
import { FormProvider, useForm } from "react-hook-form"

import { ProductFilesFormSchema, type ProductFilesFormValues } from "../schemas/ProductFilesSchema"

import { Button } from "@/components/common/Button"
import DialogHeader from "@/components/common/Modals/DialogHeader"
import { IMAGE_MIME_TYPES_WITH_PDF_AND_DOC } from "@/components/upload/constants"
import useHandleImageFileUpload from "@/components/upload/hooks/useHandleFileUpload"
import type { FieldGroupEnum } from "@/graphql/codegen/graphql"
import { ReferenceTableName } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { AddProductFileForm } from "@/screens/Products/components/Product/Information/ProductFiles/AddProductFileForm"
import {
  type FileFormData,
  canShowAddButton,
  canSubmit,
} from "@/screens/Products/components/Product/Information/utils/productFilesValidation"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface AddProductFileDialogProps {
  open: boolean
  onClose: () => void
  onRefresh: () => void
  productId: number | string
}

export const AddProductFileDialog: FC<AddProductFileDialogProps> = ({ open, onClose, onRefresh, productId }) => {
  const [fileNumber, setFileNumber] = useState<number>(1)
  const [formKey, setFormKey] = useState<number>(0)
  const [isUploading, setIsUploading] = useState<boolean>(false)
  const notify = useNotificationsStore((state) => state.enqueueNotification)

  const methods = useForm<ProductFilesFormValues>({
    resolver: zodResolver(ProductFilesFormSchema),
    mode: "onChange",
  })

  const {
    getValues,
    setValue,
    unregister,
    watch,
    formState: { isDirty, isSubmitting },
  } = methods

  const { uploadFile, onMarkFileAsNeedRemoval } = useHandleImageFileUpload({
    referenceTableName: ReferenceTableName.Product,
    allowedFileTypes: IMAGE_MIME_TYPES_WITH_PDF_AND_DOC,
  })

  const handleAddAnotherFile = (): void => {
    setFileNumber((prev) => prev + 1)
    setValue(`${fileNumber}`, null)
  }

  const handleRemoveSingleFileForm = (id: number): void => {
    const currentValues = getValues()

    const existingFields = Object.entries(currentValues)
      .filter(([key, value]) => !Number.isNaN(Number.parseInt(key)) && value !== null)
      .sort(([a], [b]) => Number.parseInt(a) - Number.parseInt(b))

    for (const key of Object.keys(currentValues)) {
      if (!Number.isNaN(Number.parseInt(key))) {
        unregister(key)
      }
    }

    let newIndex = 0
    for (const [originalKey, fieldValue] of existingFields) {
      if (Number.parseInt(originalKey) !== id) {
        setValue(`${newIndex}`, fieldValue)
        newIndex++
      }
    }

    setFileNumber((prev) => (prev > 1 ? prev - 1 : 1))
    setFormKey((prev) => prev + 1)
  }

  const handleSubmit = async (): Promise<void> => {
    const values = getValues()
    if (!canSubmit(values, isDirty, isSubmitting)) return

    setIsUploading(true)

    try {
      const filesToUpload = Object.values(values).filter(
        (fileData): fileData is Required<Pick<NonNullable<FileFormData>, "file" | "fileType">> & FileFormData =>
          !!fileData?.file && !!fileData?.fileType
      )

      await Promise.all(
        filesToUpload.map((fileData) =>
          uploadFile({
            file: fileData.file,
            type: fileData.fileType as unknown as FieldGroupEnum,
            referenceId: `${productId}`,
            label: fileData.fileDescription,
          })
        )
      )

      // Reset form and close dialog
      setFileNumber(1)
      notify({
        message: "Product information and product files updated successfully",
        type: NotificationType.Success,
      })
      setTimeout(() => {
        onRefresh()
        setIsUploading(false)
        onClose()
      }, 3000)
    } catch {
      setIsUploading(false)
      notify({
        message: "Product information and product files were not updates",
        type: NotificationType.Error,
      })
    }
  }

  const values = watch()
  const showAddButton = canShowAddButton(values, fileNumber)
  const canSubmitForm = canSubmit(values, isDirty, isSubmitting)

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth data-testid='product-file-dialog'>
      <DialogHeader title='Upload Files' onClose={onClose} />
      <DialogContent classes={{ root: "p-10" }}>
        <FormProvider {...methods}>
          <AddProductFileForm
            key={formKey}
            fileNumber={fileNumber}
            onMarkFileAsNeedRemoval={onMarkFileAsNeedRemoval}
            onRemoveSingleFileForm={handleRemoveSingleFileForm}
          />
        </FormProvider>
      </DialogContent>
      <DialogActions className='justify-between px-10 pb-10'>
        <Button appearance='outlined' onClick={onClose}>
          Cancel
        </Button>
        <Box className='flex justify-end'>
          {showAddButton && (
            <Button appearance='text' onClick={handleAddAnotherFile} startIcon={<AddOutlined />}>
              Add Another File
            </Button>
          )}
          <Button loading={isUploading} type='submit' onClick={handleSubmit} disabled={!canSubmitForm}>
            Done
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  )
}
